import type { Components, Theme } from '@mui/material/styles';
import type { PickersLocaleText } from '@mui/x-date-pickers/locales';

/**
 * Albanian texts for MUI's built-in labels (MUI ships no Albanian locale). Covers visible
 * texts and screen-reader labels so nothing appears in English.
 */
export const muiComponentsSq: Components<Theme> = {
  MuiAutocomplete: {
    defaultProps: {
      noOptionsText: 'Nuk ka zgjedhje',
      loadingText: 'Po ngarkohet…',
      clearText: 'Pastro',
      openText: 'Hap listën',
      closeText: 'Mbyll listën',
    },
  },
  MuiAlert: { defaultProps: { closeText: 'Mbyll' } },
  MuiTablePagination: {
    defaultProps: {
      labelRowsPerPage: 'Për faqe:',
      labelDisplayedRows: ({ from, to, count }) =>
        `${from}-${to} nga ${count !== -1 ? count : `më shumë se ${to}`}`,
      getItemAriaLabel: (type) =>
        ({
          first: 'Shko te faqja e parë',
          last: 'Shko te faqja e fundit',
          next: 'Faqja tjetër',
          previous: 'Faqja e mëparshme',
        })[type],
    },
  },
  MuiPagination: {
    defaultProps: {
      getItemAriaLabel: (type, page, selected) =>
        type === 'page'
          ? `${selected ? '' : 'Shko te '}faqja ${page}`
          : ((
              {
                first: 'Faqja e parë',
                last: 'Faqja e fundit',
                next: 'Faqja tjetër',
                previous: 'Faqja e mëparshme',
              } as Record<string, string>
            )[type] ?? ''),
    },
  },
  MuiBreadcrumbs: { defaultProps: { expandText: 'Shfaq rrugën' } },
  MuiChip: { styleOverrides: { root: { fontWeight: 500 } } },
};

const view = (v: string) =>
  ({
    year: 'vitin',
    month: 'muajin',
    day: 'ditën',
    hours: 'orën',
    minutes: 'minutat',
    seconds: 'sekondat',
  })[v] ?? v;

export const pickersSq: Partial<PickersLocaleText> = {
  previousMonth: 'Muaji i kaluar',
  nextMonth: 'Muaji tjetër',
  openPreviousView: 'Pamja e mëparshme',
  openNextView: 'Pamja tjetër',
  calendarViewSwitchingButtonAriaLabel: (v) =>
    v === 'year' ? 'Po shihni vitet. Kaloni te kalendari' : 'Po shihni kalendarin. Kaloni te vitet',
  start: 'Fillimi',
  end: 'Mbarimi',
  cancelButtonLabel: 'Anulo',
  clearButtonLabel: 'Pastro',
  okButtonLabel: 'Në rregull',
  todayButtonLabel: 'Sot',
  nextStepButtonLabel: 'Tjetër',
  datePickerToolbarTitle: 'Zgjidhni datën',
  dateTimePickerToolbarTitle: 'Zgjidhni datën dhe orën',
  timePickerToolbarTitle: 'Zgjidhni orën',
  clockLabelText: (v, formatted) =>
    `Zgjidhni ${view(v)}. ${formatted ? `Ora e zgjedhur: ${formatted}` : 'Nuk është zgjedhur ora'}`,
  hoursClockNumberText: (h) => `ora ${h}`,
  minutesClockNumberText: (m) => `${m} minuta`,
  secondsClockNumberText: (s) => `${s} sekonda`,
  selectViewText: (v) => `Zgjidhni ${view(v)}`,
  calendarWeekNumberHeaderLabel: 'Java',
  calendarWeekNumberAriaLabelText: (w) => `Java ${w}`,
  openDatePickerDialogue: (d) => (d ? `Zgjidhni datën, e zgjedhur: ${d}` : 'Zgjidhni datën'),
  openTimePickerDialogue: (d) => (d ? `Zgjidhni orën, e zgjedhur: ${d}` : 'Zgjidhni orën'),
  fieldClearLabel: 'Pastro',
  timeTableLabel: 'zgjidhni orën',
  dateTableLabel: 'zgjidhni datën',
  fieldYearPlaceholder: (p) => 'V'.repeat(p.digitAmount),
  fieldMonthPlaceholder: (p) => (p.contentType === 'letter' ? 'MMMM' : 'MM'),
  fieldDayPlaceholder: () => 'DD',
  fieldHoursPlaceholder: () => 'oo',
  fieldMinutesPlaceholder: () => 'mm',
  fieldSecondsPlaceholder: () => 'ss',
  year: 'Viti',
  month: 'Muaji',
  day: 'Dita',
  weekDay: 'Dita e javës',
  hours: 'Ora',
  minutes: 'Minutat',
  seconds: 'Sekondat',
  empty: 'Bosh',
};
